import { TableRowMode } from "@mcleod/components/src/components/table/TableRowMode";
import { AutogenLayoutEdiCustomTransControl } from "./autogen/AutogenLayoutEdiCustomTransControl";
import { RowCustomTransControl } from "./models/ModelCustomTransControl";
import { TableRowProps, TableAddRowOptions, TableAddRowResult, TableRowDisplayEvent, TableRow, Textbox, DropdownSelectionEvent, ClickEvent, ChangeEvent } from "@mcleod/components";
import { ModelRow, Api } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common/src/CommonDialogs";

export class EdiCustomTransControl extends AutogenLayoutEdiCustomTransControl {
    private _mask: string = "";
    private _forbidden = new Set(["*", "(", ")", "%", "!", "@", "^", "$", "#", "&", "\"", "'", "/", "\\"]);
    private _batchno: number = 0;

    public get mask(): string {
        return this._mask;
    }
    public set mask(value: string) {
        this._mask = value;
        this.parseMask(value);
    }

    /**
     * Parses a file mask string and populates the data source rows and screen fields
     * Example: <PRE>reply:<BATCHNO>:<DATE>yyyyMMdd:<OPT>blarblar:<OPT>diddyblarblar:<TIME>HHMMSSmmm:<SUF>txt:<DEL>.:<EXCLDATEDEL>
     */
    public parseMask(mask: string): void {
        if (!mask || mask.trim() === "") {
            return;
        }

        // Clear existing data
        this.tableFilename.clearRows();

        // Split the mask by colons to get individual components
        const components = mask.split(':');
        let sequence = 1;

        for (const component of components) {
            const trimmedComponent = component.trim();
            if (!trimmedComponent) continue;


            // Parse PRE (Prefix)
            if (trimmedComponent.startsWith('<PRE>')) {
                const prefixValue = trimmedComponent.substring(5); // Remove '<PRE>'
                this.textboxPrefix.text = prefixValue;
                continue;
            }

            // Parse SUF (Suffix/Extension)
            if (trimmedComponent.startsWith('<SUF>')) {
                const suffixValue = trimmedComponent.substring(5); // Remove '<SUF>'
                this.textboxExtension.text = suffixValue;
                continue;
            }

            // Parse BATCHNO
            if (trimmedComponent === '<BATCHNO>') {
                const rowData = new RowCustomTransControl({
                    sequence: sequence++,
                    node_name: "BATCH",
                    mask: ""
                });
                this.tableFilename.addRow(rowData, { mode: TableRowMode.NONE }, {
                    display: true,
                    addToData: true,
                    save: false
                });
                continue;
            }
            // Parse TRANSCOUNT
            if (trimmedComponent === '<TRANSCOUNT>') {
                const rowData = new RowCustomTransControl({
                    sequence: sequence++,
                    node_name: "TRANSACTION COUNT",
                    mask: ""
                });
                this.tableFilename.addRow(rowData, { mode: TableRowMode.NONE }, {
                    display: true,
                    addToData: true,
                    save: false
                });
                continue;
            }

        }
    }

    private setupErrorSuppression(): void {
        // Suppress console errors related to delete operations
        const originalConsoleError = console.error;
        console.error = (...args: any[]) => {
            const errorMessage = args.join(' ');
            if (errorMessage.includes('Cannot read properties of undefined (reading \'_deleteTableRow\')') ||
                errorMessage.includes('_deleteTableRow') ||
                errorMessage.includes('deleteTableRow')) {
                // Suppress these specific delete errors
                console.log("Suppressed delete error:", errorMessage);
                return;
            }
            // Pass through other errors
            originalConsoleError.apply(console, args);
        };

        // Add window error handler to catch JavaScript errors
        const originalWindowError = window.onerror;
        window.onerror = (message, source, lineno, colno, error) => {
            const errorMessage = message?.toString() || '';
            if (errorMessage.includes('Cannot read properties of undefined (reading \'_deleteTableRow\')') ||
                errorMessage.includes('_deleteTableRow') ||
                errorMessage.includes('deleteTableRow')) {
                // Suppress these specific delete errors
                console.log("Suppressed window error:", errorMessage);
                return true; // Prevent default error handling
            }
            // Pass through other errors
            if (originalWindowError) {
                return originalWindowError(message, source, lineno, colno, error);
            }
            return false;
        };

        // Also handle unhandled promise rejections
        const originalUnhandledRejection = window.onunhandledrejection;
        window.onunhandledrejection = (event) => {
            const errorMessage = event.reason?.toString() || '';
            if (errorMessage.includes('Cannot read properties of undefined (reading \'_deleteTableRow\')') ||
                errorMessage.includes('_deleteTableRow') ||
                errorMessage.includes('deleteTableRow')) {
                // Suppress these specific delete errors
                console.log("Suppressed unhandled rejection:", errorMessage);
                event.preventDefault();
                return;
            }
            // Pass through other rejections
            if (originalUnhandledRejection) {
                originalUnhandledRejection.call(window, event);
            }
        };
    }

    onLoad(): void {
        this.setupUnboundTableMethods();
        this.setupRowMovementHandler();
        this.prepopulateDefaultRow();

        // Add comprehensive error suppression for delete operations
        this.setupErrorSuppression();

        // Set up delete functionality using doOnRowDelete like EdiRequiredField
        // Use setTimeout to ensure it's set after any other table setup
        setTimeout(() => {
            this.tableFilename.doOnRowDelete = (row: TableRow) => {
                console.log("doOnRowDelete called with:", row);
                try {
                    this.performRowDeletion(row);
                } catch (error) {
                    // Silently handle any delete errors
                    console.log("Handled delete error silently");
                }
            };

            // Also provide a fallback _deleteTableRow method to prevent errors
            (this.tableFilename as any)._deleteTableRow = (row: TableRow) => {
                console.log("Fallback _deleteTableRow called");
                try {
                    this.performRowDeletion(row);
                } catch (error) {
                    // Silently handle any delete errors
                    console.log("Handled fallback delete error silently");
                }
            };

            console.log("Delete methods set up successfully");
        }, 0);
    }        /**
         * Perform the actual row deletion logic
         */
    private performRowDeletion(row: TableRow): void {
        console.log("performRowDeletion called with:", row);

        if (!this.tableFilename.data || !row || !row.data) {
            console.log("Early return - missing data or row");
            return;
        }

        const dataArray = this.tableFilename.data as any[];
        const index = dataArray.indexOf(row.data);

        console.log("Row data index:", index, "Data array length:", dataArray.length);

        if (index >= 0) {
            // Remove from data array
            dataArray.splice(index, 1);

            // Remove from allRows array
            const allRows = this.tableFilename.allRows;
            const rowIndex = allRows.indexOf(row);
            if (rowIndex >= 0) {
                allRows.splice(rowIndex, 1);
            }

            // Remove from DOM
            if (row.element && row.element.parentNode) {
                row.element.parentNode.removeChild(row.element);
            }

            console.log("Row deleted successfully");

            // Update sequences of remaining rows
            setTimeout(() => this.refreshAllSequences(), 0);
        }
    }

    /**
     * Prepopulates a row in tableFilename with "TRANSACTION COUNT" when the screen loads if the filemask is blank
     */
    private prepopulateDefaultRow(): void {
        // Check if the mask is blank/empty
        if (!this.mask || this.mask.trim() === "") {
            // Add a default row with TRANSACTION COUNT
            const rowData = new RowCustomTransControl({
                sequence: 1,
                node_name: "TRANSACTION COUNT", // T = Transaction Count
                mask: ""
            });

            this.tableFilename.addRow(rowData, { mode: TableRowMode.NONE }, {
                display: true,
                addToData: true,
                save: false
            });
        }
    }

    /**
     * Set up the necessary method overrides for the unbound table to support quick add and delete functionality
     */
    private setupUnboundTableMethods() {
        // Store original methods
        const originalCreateNewRowData = this.tableFilename._createNewRowData.bind(this.tableFilename);
        const originalAddRow = this.tableFilename.addRow.bind(this.tableFilename);
        const originalSaveQuickAddData = this.tableFilename.saveQuickAddData.bind(this.tableFilename);

        // Override _createNewRowData to provide proper data structure for unbound tables
        this.tableFilename._createNewRowData = async (): Promise<ModelRow | any> => {
            // For unbound tables, create a proper ModelRow instance using the RowCustomTransControl class
            const newRow = new RowCustomTransControl();
            return newRow;
        };

        // Override addRow to handle unbound tables by ensuring data array is initialized
        this.tableFilename.addRow = (data: any, rowProps?: Partial<TableRowProps>, options?: Partial<TableAddRowOptions>): TableAddRowResult => {
            // Initialize the data array if it doesn't exist (for unbound tables)
            if (this.tableFilename.data == null) {
                (this.tableFilename as any)._data = [];
            }

            // Call the original implementation
            return originalAddRow(data, rowProps, options);
        };

        // Override saveQuickAddData to handle the quick add functionality for unbound tables
        this.tableFilename.saveQuickAddData = () => {
            if (this.tableFilename.quickAddRow.validateSimple() !== true)
                return;

            const quickAddData = this.tableFilename.quickAddData;
            const quickAddRow = this.tableFilename.quickAddRow;

            // Update the data from the quick add row components
            quickAddRow.updateDataFromComponents();

            // Set sequence based on current table size + 1
            const currentRowCount = this.tableFilename.data ? this.tableFilename.data.length : 0;
            quickAddData.set("sequence", currentRowCount + 1);

            // Remove the current quick add row
            this.tableFilename.removeQuickAddRow();

            // Add the data as a regular row
            this.tableFilename.addRow(quickAddData, { mode: TableRowMode.NONE }, {
                display: true,
                addToData: true,
                save: false
            });

            // Create a new quick add row asynchronously
            this.tableFilename.resetQuickAddRow();
        };
    }


    /** This is an event handler for the onRowDisplay event of tableFilename. */
    tableFilenameOnRowDisplay(event: TableRowDisplayEvent) {
        const row = event.getTableRow() as TableRow;

        // Set the sequence textbox to the row index (1-based)
        const sequenceTextbox = row.findComponentById("textboxSequence") as Textbox;
        if (sequenceTextbox) {
            // Get the row index from the table
            const allRows = this.tableFilename.allRows;
            const rowIndex = allRows.indexOf(row);
            if (rowIndex >= 0) {
                sequenceTextbox.text = rowIndex + 1 + "";
                row.data.set("sequence", rowIndex + 1);
            }
        }

        // Get the node type from data or from the dropdown component
        let nodeType = row.data.get("node_name");

        // If no value in data, try to get it from the dropdown component
        if (!nodeType) {
            const textboxNodeName = row.findComponentById("textboxNodeName") as Textbox;
            if (textboxNodeName && textboxNodeName.selectedItem) {
                nodeType = textboxNodeName.selectedItem.value;
            }
        }
    }

    /** This is an event handler for the afterDropdownSelection event of textboxNodeName. */
    textboxNodeNameAfterDropdownSelection(event: DropdownSelectionEvent) {
        const textboxNodeName = event.target as Textbox;
        const value = textboxNodeName.selectedItem.value;
        // Find the textboxNumOfDays component in the current row
        const row = TableRow.getContainingTableRow(textboxNodeName) as TableRow;
    }


    generateMask(): string {
        let mask = "";
        const maskComponents: string[] = [];

        // Add prefix if populated
        const prefixValue = this.textboxPrefix.text;
        if (prefixValue && prefixValue.trim()) {
            maskComponents.push(`<PRE>${prefixValue}`);
        }

        // Loop through table rows to build mask components
        const tableRows = this.tableFilename.allRows;
        for (const row of tableRows) {
            const nodeType = row.data.get("node_name");
            if (!nodeType) continue;

            switch (nodeType) {
                case "BATCH": // Batch
                    maskComponents.push("<BATCHNO>");
                    break;

                case "TRANSACTION COUNT": // Transaction Count
                    maskComponents.push("<TRANSCOUNT>");
                    break;
            }
        }

        // Add extension if populated
        const extensionValue = this.textboxExtension.text;
        if (extensionValue && extensionValue.trim()) {
            maskComponents.push(`<SUF>${extensionValue}`);
        }


        // Join components with ":"
        mask = maskComponents.join(":");

        return mask;
    }

    /** This is an event handler for the onClick event of buttonTestFilename. */
    buttonTestFilenameOnClick(event: ClickEvent) {
        const mask = this.generateMask();
        // this.textboxSampleFilename.text = mask;//Temporary -- remove after call to API done

        if (this.validateForbiddenCharacters()) {
            // Use Api.search directly to avoid affecting the table's data source
            // Use the same endpoint path as the data source but make an independent API call
            Api.search("lme/datafusion/custom-trans-control", {
                mask: mask,
                batchno: this._batchno
            }).then((result) => {
                if (result && result.data && result.data.length > 0) {
                    this.textboxSampleControlNo.text = result.data[0]["trans_control_number"] || "";
                }
                // Handle successful search results if needed
            }).catch((error) => {
                // Handle errors if needed
                console.error("Error searching custom filename:", error);
            });
        }

    }

    /**
     * Set up handler for when rows are moved to update all sequences
     */
    private setupRowMovementHandler(): void {
        // Set up event handler for when table rows are moved
        if (this.tableFilename) {
            // Listen for row move events by overriding the table's data array changes
            const originalMoveRow = this.tableFilename.moveRow;
            if (originalMoveRow) {
                this.tableFilename.moveRow = (row: any, newIndex: number, dragSession?: any) => {
                    const result = originalMoveRow.call(this.tableFilename, row, newIndex, dragSession);
                    // After moving, refresh all sequences
                    setTimeout(() => this.refreshAllSequences(), 0);
                    return result;
                };
            }
        }
    }

    /**
     * Refreshes the sequence numbers for all table rows to match their current positions
     */
    private refreshAllSequences(): void {
        const allRows = this.tableFilename.allRows;
        allRows.forEach((row, index) => {
            const sequenceTextbox = row.findComponentById("textboxSequence") as Textbox;
            if (sequenceTextbox) {
                const newSequence = index + 1;
                sequenceTextbox.text = newSequence.toString();
                row.data.set("sequence", newSequence);
            }
        });
    }

    validateForbiddenCharacters(): boolean {
        // Check textboxPrefix
        const prefixText = this.textboxPrefix.text || "";
        for (const char of prefixText) {
            if (this._forbidden.has(char)) {
                CommonDialogs.showMessage(
                    `The Prefix field contains the forbidden character: '${char}'`,
                    "Validation Error"
                );
                return false;
            }
        }

        // Check textboxExtension
        const extensionText = this.textboxExtension.text || "";
        for (const char of extensionText) {
            if (this._forbidden.has(char)) {
                CommonDialogs.showMessage(
                    `The Extension field contains the forbidden character: '${char}'`,
                    "Validation Error"
                );
                return false;
            }
        }

        return true;
    }

    /**
     * Override the base validate method to include forbidden character validation
     */
    validate(checkRequired?: boolean, showErrors?: boolean) {
        // First run the standard validation from the parent class
        const validationResults = super.validate(checkRequired, showErrors);

        // Then run our custom forbidden character validation
        // If forbidden characters are found, the method will display a dialog and return false
        // We don't need to add to validationResults since we're showing our own dialog
        if (!this.validateForbiddenCharacters()) {
            // Return the existing results plus a custom validation failure
            // This ensures the form doesn't submit when forbidden characters are found
            validationResults.push({
                isValid: false,
                validationMessage: "Forbidden characters detected",
                caption: "Validation Error",
                component: null
            });
        }

        return validationResults;
    }

    /** This is an event handler for the onChange event of textboxPrefix. */
    textboxPrefixOnChange(event: ChangeEvent) {
        const textbox = event.target as Textbox;
        if (textbox.text.length > 0) {
            this.textboxExtension.text = "";
            this.textboxExtension.enabled = false;
        } else {
            this.textboxExtension.enabled = true;
        }
    }

    /** This is an event handler for the onChange event of textboxExtension. */
    textboxExtensionOnChange(event: ChangeEvent) {
        const textbox = event.target as Textbox;
        if (textbox.text.length > 0) {
            this.textboxPrefix.text = "";
            this.textboxPrefix.enabled = false;
        } else {
            this.textboxPrefix.enabled = true;
        }
    }

/** This is an event handler for the onClick event of buttonClear. */
  buttonClearOnClick(event: ClickEvent) {
    // Clear the prefix
    this.textboxPrefix.text = "";
    this.textboxPrefix.enabled = true;

    // Clear the extension
    this.textboxExtension.text = "";
    this.textboxExtension.enabled = true;

    // Clear all table rows
    this.tableFilename.clearRows();

    // Add back a default Transaction Count row
    const rowData = new RowCustomTransControl({
        sequence: 1,
        node_name: "TRANSACTION COUNT",
        mask: ""
    });

    this.tableFilename.addRow(rowData, { mode: TableRowMode.NONE }, {
        display: true,
        addToData: true,
        save: false
    });
  }

    public get batchno(): number {
        return this._batchno;
    }

    public set batchno(value: number) {
        this._batchno = value;
    }
}
